import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useToast } from "@/components/ui/use-toast";
import { useLanguage } from '@/contexts/LanguageContext';

const initialOnboardingStatus = {
  termsAgreed: false,
  profileComplete: false,
  creditReportLinked: false,
  firstGoalSet: false,
  dashboardTourCompleted: false,
  overallOnboardingComplete: false,
};

export const useOnboardingStatus = (user, profile) => {
  const [onboardingStatus, setOnboardingStatus] = useState(initialOnboardingStatus);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [errorStatus, setErrorStatus] = useState(null);
  const { toast } = useToast();
  const { t } = useLanguage();

  const calculateOverallCompletion = useCallback((status) => {
    return (
      status.termsAgreed &&
      status.profileComplete &&
      status.creditReportLinked &&
      status.firstGoalSet &&
      status.dashboardTourCompleted
    );
  }, []);

  const fetchOnboardingStatus = useCallback(async () => {
    if (!user || !profile) {
      setOnboardingStatus(initialOnboardingStatus);
      setLoadingStatus(false);
      return;
    }

    setLoadingStatus(true);
    setErrorStatus(null);
    try {
      const computedProfileComplete = !!(profile.first_name && profile.last_name);

      const newStatus = {
        termsAgreed: !!profile.terms_agreed_at && !!profile.terms_version_agreed,
        profileComplete: computedProfileComplete,
        creditReportLinked: !!profile.has_added_credit_score || !!profile.has_added_accounts,
        firstGoalSet: !!profile.has_set_goal,
        dashboardTourCompleted: !!profile.has_completed_onboarding_tour,
        overallOnboardingComplete: !!profile.overall_onboarding_complete
      };
      
      // --- Sync has_set_goal with goals table ---
      if (!newStatus.firstGoalSet) {
        const { data: goals, error: goalsError } = await supabase
          .from('goals')
          .select('id')
          .eq('user_id', user.id)
          .limit(1);
        if (!goalsError && goals && goals.length > 0) {
          await supabase
            .from('profiles')
            .update({ has_set_goal: true })
            .eq('id', user.id);
          newStatus.firstGoalSet = true;
        }
      }
      // --- END SYNC ---

      newStatus.overallOnboardingComplete = calculateOverallCompletion(newStatus);

      setOnboardingStatus(newStatus);

      if (profile.profile_complete !== computedProfileComplete) {
        const { error: pcUpdateError } = await supabase
          .from('profiles')
          .update({ profile_complete: computedProfileComplete })
          .eq('id', user.id);
        if (pcUpdateError) {
          console.warn("Failed to sync profile_complete to DB:", pcUpdateError);
        }
      }

      if (profile.overall_onboarding_complete !== newStatus.overallOnboardingComplete) {
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ overall_onboarding_complete: newStatus.overallOnboardingComplete })
          .eq('id', user.id);
        if (updateError) {
          console.warn("Failed to sync overall_onboarding_complete to DB:", updateError);
        }
      }

    } catch (err) {
      console.error("Error fetching/calculating onboarding status:", err);
      setErrorStatus(t('common.error', 'Failed to load onboarding status.'));
      setOnboardingStatus(initialOnboardingStatus);
    } finally {
      setLoadingStatus(false);
    }
  }, [user, profile, t, calculateOverallCompletion]);

  // Avoid repeated re-fetch loops triggered by profile realtime updates.
  // Run once per user session (or when profile identity changes).
  const ranRef = useRef(false);
  useEffect(() => {
    if (!user || !profile) {
      ranRef.current = false;
      return;
    }
    if (!ranRef.current) {
      ranRef.current = true;
      fetchOnboardingStatus();
    }
  }, [user?.id, profile?.id, fetchOnboardingStatus]);

  const updateOnboardingStatus = useCallback(async (step, value) => {
    if (!user) {
      toast({ title: t('common.error', 'Error'), description: t('common.loginPrompt', 'Please log in.'), variant: "destructive" });
      return false;
    }
    
    const dbFieldMap = {
      termsAgreed: { terms_agreed_at: new Date().toISOString(), terms_version_agreed: profile?.terms_version_agreed || '1.0' }, 
      profileComplete: { profile_complete: value },
      creditReportLinked: value ? (profile?.has_added_credit_score ? { has_added_credit_score: true } : { has_added_accounts: true }) : { has_added_credit_score: false, has_added_accounts: false },
      firstGoalSet: { has_set_goal: value },
      dashboardTourCompleted: { has_completed_onboarding_tour: value },
    };

    let updatePayload = {};
    if (dbFieldMap[step]) {
      updatePayload = dbFieldMap[step];
    } else {
      console.warn(`Unknown onboarding step: ${step}`);
      return false;
    }

    try {
      setLoadingStatus(true);
      const { error: updateError } = await supabase
        .from('profiles')
        .update(updatePayload)
        .eq('id', user.id);

      if (updateError) throw updateError;

      const updatedStatusPart = { [step]: value };
      const newProvisionalStatus = { ...onboardingStatus, ...updatedStatusPart };
      const newOverallCompletion = calculateOverallCompletion(newProvisionalStatus);
      
      if (newOverallCompletion !== onboardingStatus.overallOnboardingComplete) {
        const { error: overallUpdateError } = await supabase
          .from('profiles')
          .update({ overall_onboarding_complete: newOverallCompletion })
          .eq('id', user.id);
        if (overallUpdateError) {
            console.warn("Failed to update overall_onboarding_complete in DB after step update:", overallUpdateError);
        }
         setOnboardingStatus(prev => ({ ...prev, ...updatedStatusPart, overallOnboardingComplete: newOverallCompletion }));
      } else {
         setOnboardingStatus(prev => ({ ...prev, ...updatedStatusPart }));
      }

      toast({ title: t('common.success', 'Success!'), description: `${t(step, step)} status updated.` });
      return true;
    } catch (err) {
      console.error(`Error updating onboarding step ${step}:`, err);
      toast({ title: t('common.error', 'Error'), description: `Failed to update ${t(step, step)} status.`, variant: "destructive" });
      setErrorStatus(`Failed to update ${step}`);
      return false;
    } finally {
      setLoadingStatus(false);
    }
  }, [user, profile, toast, t, onboardingStatus, calculateOverallCompletion]);
  
  return { onboardingStatus, loadingStatus, errorStatus, updateOnboardingStatus, refetchOnboardingStatus: fetchOnboardingStatus };
};
  
